import type { TFunction } from 'i18next';

import { useState } from 'react';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  EXAMPLE_GROUP_ID,
  SPLIT_CHANGE_KEYS,
  isGroupGoneSyncError,
  offlineExpenseService,
  queryKeys,
  withoutLineItems,
} from '@splitfiesta/core';
import type { OfflinePredictionMeta } from '@splitfiesta/core';
import { i18n } from '@splitfiesta/i18n/web';
import type {
  CommentResponseDto,
  CreateExpenseDto,
  ExpenseResponseDto,
  GetUploadUrlDto,
  PaginatedResponseDto,
  PendingConversionFields,
  SuccessResponseDto,
  UpdateExpenseDto,
  UploadUrlResponseDto,
} from '@splitfiesta/shared';

import { checkIsOnline } from '@/hooks/useNetworkStatus';
import { useTypedApi } from '@/hooks/useTypedApi';

import {
  trackExpenseCreated,
  trackExpenseDeleted,
  trackExpenseUpdated,
} from '@/lib/analytics';

import { useAuthStore } from '@/stores/authStore';
import { useOfflineOwnerId, useOfflineStore } from '@/stores/offlineStore';

import { handleApiError } from '@/utils/errorUtils';
import {
  convertHeicToJpeg,
  isHeicFile,
  optimizeImageForUpload,
} from '@/utils/imageOptimization';
import { toast } from '@/utils/toast';
import { isExpenseResponse } from '@/utils/typeGuards';

interface ExpensesParams {
  category?: string;
  subcategory?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
}

// Fetch expenses for a group with infinite scroll
export function useGroupExpensesInfiniteQuery(
  groupId: string,
  params?: ExpensesParams,
  enabled = true
) {
  const api = useTypedApi();

  return useInfiniteQuery({
    queryKey: queryKeys.expensesByGroup(groupId, params),
    queryFn: async ({ pageParam }) => {
      const queryParams = new URLSearchParams();
      if (params?.category) queryParams.append('category', params.category);
      if (params?.subcategory)
        queryParams.append('subcategory', params.subcategory);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      if (params?.search) queryParams.append('search', params.search);
      if (pageParam) queryParams.append('cursor', pageParam);
      queryParams.append('limit', String(params?.limit || 20));

      const url = `/expenses/group/${groupId}?${queryParams.toString()}`;
      const response =
        await api.get<
          SuccessResponseDto<PaginatedResponseDto<ExpenseResponseDto>>
        >(url);

      // Extract standardized paginated response
      const responseData = (
        response as SuccessResponseDto<PaginatedResponseDto<ExpenseResponseDto>>
      ).data;

      return {
        expenses: responseData.items || [],
        hasMore: responseData.hasMore || false,
        nextCursor: responseData.cursor,
      };
    },
    getNextPageParam: lastPage =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!groupId,
  });
}

/** The API's ceiling for a signed upload size (IMAGE_UPLOAD_MAX_BYTES, not exported to shared). */
export const SIGNED_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** List rows never carry line items; only the detail cache does. */
const toListRow = (expense: ExpenseResponseDto) =>
  withoutLineItems(expense) as ExpenseResponseDto;

// Fetch single expense
export function useExpenseQuery(
  expenseId: string,
  enabled = true,
  options: { refetchOnMount?: 'always' } = {}
) {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const user = useAuthStore(state => state.user);
  const isLocal = offlineExpenseService.isLocalId(expenseId);
  // A queued `local_*` expense has no server row; the edit page reaches it
  // from the failed-sync banner, so serve the queued copy instead.
  const pending = useOfflineStore(state =>
    isLocal ? state.getExpenseByLocalId(expenseId, user?.id) : undefined
  );
  const hasHydrated = useOfflineStore(state => state.hasHydrated);
  // Read once per expense id, like `initialData`: the edit form seeds its
  // fields from this, and a sync attempt rewriting the queued row must not
  // re-seed over the user's typing. Seeded during render (React's "adjust
  // state on prop change" pattern) because the row may only appear once
  // the store has hydrated.
  const [seeded, setSeeded] = useState<
    { id: string; data: ExpenseResponseDto } | undefined
  >(undefined);
  if (isLocal && pending && seeded?.id !== expenseId) {
    setSeeded({
      id: expenseId,
      data: offlineExpenseService.toDisplayExpenseFromCache(
        pending,
        user,
        queryClient
      ),
    });
  }
  const offlineExpense =
    isLocal && seeded?.id === expenseId ? seeded.data : undefined;

  const query = useQuery({
    queryKey: queryKeys.expense(expenseId),
    queryFn: async () => {
      const response = await api.expenses.getById(expenseId);
      // Handle both wrapped and unwrapped responses
      const expenseData = response.data || response;

      if (!isExpenseResponse(expenseData)) {
        // Return the data anyway if it looks like it has the right structure
        if (
          expenseData &&
          typeof expenseData === 'object' &&
          'id' in expenseData
        ) {
          return expenseData as unknown as ExpenseResponseDto;
        }
        throw new Error('Invalid expense response');
      }
      return expenseData;
    },
    enabled: enabled && !!expenseId && !isLocal,
    ...(options.refetchOnMount
      ? { refetchOnMount: options.refetchOnMount }
      : {}),
  });
  if (!isLocal) return query;
  // The offline store hydrates asynchronously, and `initialData` is read
  // only when the query is first created, so on a cold load of a local
  // id's URL it would stay empty forever. Serve the queued copy directly.
  return {
    ...query,
    data: offlineExpense,
    isLoading: !hasHydrated,
    isPending: !offlineExpense,
    isSuccess: !!offlineExpense,
    status: offlineExpense ? 'success' : 'pending',
  } as typeof query;
}

/**
 * Input shape for the create-expense mutation. `predictionMeta` is
 * optional — pass it from the form when the category was auto-picked
 * (keyword / cloud) so the offline queue carries the source +
 * confidence. Omit when the user picked manually; the sync-time cloud
 * upgrade keys off the absence as "don't overwrite".
 */
export interface CreateExpenseInput {
  data: CreateExpenseDto;
  predictionMeta?: OfflinePredictionMeta;
}

// Create expense mutation with offline support
export function useCreateExpenseMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const addPendingExpense = useOfflineStore(state => state.addPendingExpense);
  const user = useAuthStore(state => state.user);

  return useMutation({
    // Override networkMode to 'always' so our offline handling code runs
    // instead of React Query pausing the mutation when offline
    networkMode: 'always',
    mutationFn: async (input: CreateExpenseInput | CreateExpenseDto) => {
      // Back-compat: allow callers to still pass a raw CreateExpenseDto.
      // Detect by the presence of `data` (the new wrapper shape).
      const isWrapped =
        typeof input === 'object' &&
        input !== null &&
        'data' in input &&
        typeof (input as CreateExpenseInput).data === 'object';
      const data = isWrapped
        ? (input as CreateExpenseInput).data
        : (input as CreateExpenseDto);
      const predictionMeta = isWrapped
        ? (input as CreateExpenseInput).predictionMeta
        : undefined;

      const queueOffline = (): ExpenseResponseDto => {
        if (!user?.id) throw new Error('Not signed in');
        const pendingExpense = offlineExpenseService.createPendingExpense(
          data,
          user.id,
          predictionMeta
        );
        addPendingExpense(pendingExpense);

        // Return a display-friendly version that looks like ExpenseResponseDto
        return offlineExpenseService.toDisplayExpense(
          pendingExpense,
          user.id,
          user.name
        ) as unknown as ExpenseResponseDto;
      };

      const create = async (): Promise<ExpenseResponseDto> => {
        const response = await api.expenses.create(data);
        const expenseData = response.data || response;

        if (!isExpenseResponse(expenseData)) {
          // Return the data anyway if it looks valid
          if (
            expenseData &&
            typeof expenseData === 'object' &&
            'id' in expenseData
          ) {
            return expenseData as unknown as ExpenseResponseDto;
          }
          throw new Error('Invalid expense response');
        }
        return expenseData;
      };

      // The example is answered by the in-memory sandbox, which works
      // offline; a queued example row would sync to a group that does not exist.
      if (data.groupId === EXAMPLE_GROUP_ID) return create();

      // OFFLINE PATH: Save to local store when offline
      if (!checkIsOnline()) return queueOffline();

      // ONLINE PATH: Normal API call with offline fallback
      try {
        return await create();
      } catch (error) {
        // Fall back to offline save if the API call failed due to connectivity
        const err = error as { code?: string; message?: string };
        const isNetworkFailure =
          err.code === 'OFFLINE' ||
          err.code === 'NETWORK_ERROR' ||
          err.code === 'TIMEOUT' ||
          err.message === 'Failed to fetch';

        if (isNetworkFailure) return queueOffline();

        throw error;
      }
    },
    onSuccess: expense => {
      // Check if this is an offline expense (has isOffline flag)
      const isOfflineExpense = 'isOffline' in expense && expense.isOffline;

      // Track analytics (only for online expenses)
      if (!isOfflineExpense) {
        trackExpenseCreated(
          expense.groupId,
          expense.amount,
          expense.currency,
          expense.category
        );
      }

      // Optimistically append new expense to the cache
      queryClient.setQueriesData(
        {
          queryKey: ['splitfiesta', 'expenses', 'group', expense.groupId],
          exact: false,
        },
        (
          oldData:
            | {
                pages: Array<{
                  expenses: ExpenseResponseDto[];
                  nextCursor?: string;
                  hasMore: boolean;
                }>;
                pageParams: unknown[];
              }
            | undefined
        ) => {
          if (!oldData?.pages) return oldData;

          // Add new expense to the first page
          return {
            ...oldData,
            pages: oldData.pages.map(
              (
                page: {
                  expenses: ExpenseResponseDto[];
                  nextCursor?: string;
                  hasMore: boolean;
                },
                index: number
              ) => {
                if (index === 0) {
                  return {
                    ...page,
                    expenses: [toListRow(expense), ...(page.expenses || [])],
                  };
                }
                return page;
              }
            ),
          };
        }
      );

      // Only invalidate server-dependent queries if NOT an offline expense
      // Offline expenses haven't affected server state yet
      if (!isOfflineExpense) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupBalances(expense.groupId),
        });
        // Cross-group totals on the home global-balances card.
        queryClient.invalidateQueries({
          queryKey: queryKeys.userNetBalance(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupWithDashboard(expense.groupId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupReports.all(expense.groupId),
          exact: false,
        });
      }
    },
  });
}

// Update expense mutation
export function useUpdateExpenseMutation(expenseId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const updatePendingExpense = useOfflineStore(
    state => state.updatePendingExpense
  );
  const getExpenseByLocalId = useOfflineStore(
    state => state.getExpenseByLocalId
  );
  const user = useAuthStore(state => state.user);
  const isLocal = offlineExpenseService.isLocalId(expenseId);

  return useMutation({
    // A queued expense is patched locally, so it must not wait for
    // connectivity like a server update does.
    networkMode: isLocal ? 'always' : 'online',
    // Pending-conversion fields ride along only for a queued expense.
    mutationFn: async (data: UpdateExpenseDto & PendingConversionFields) => {
      if (isLocal) {
        updatePendingExpense(
          expenseId,
          offlineExpenseService.toPendingPatch(data),
          { retry: true }
        );
        const pending = getExpenseByLocalId(expenseId, user?.id);
        if (!pending) {
          throw new Error('Pending expense not found');
        }
        return offlineExpenseService.toDisplayExpenseFromCache(
          pending,
          user,
          queryClient
        );
      }
      const response = await api.expenses.update(expenseId, data);
      const expenseData = response.data || response;

      if (!isExpenseResponse(expenseData)) {
        // Return the data anyway if it looks valid
        if (
          expenseData &&
          typeof expenseData === 'object' &&
          'id' in expenseData
        ) {
          return expenseData as unknown as ExpenseResponseDto;
        }
        throw new Error('Invalid expense response');
      }
      return expenseData;
    },
    onSuccess: expense => {
      if (!isLocal) trackExpenseUpdated(expense.groupId, expenseId);

      // Update the specific expense in cache
      queryClient.setQueryData(queryKeys.expense(expenseId), expense);

      // Update expense in the list cache without invalidating
      queryClient.setQueriesData(
        {
          queryKey: ['splitfiesta', 'expenses', 'group', expense.groupId],
          exact: false,
        },
        (
          oldData:
            | {
                pages: Array<{
                  expenses: ExpenseResponseDto[];
                  nextCursor?: string;
                  hasMore: boolean;
                }>;
                pageParams: unknown[];
              }
            | undefined
        ) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map(
              (page: {
                expenses: ExpenseResponseDto[];
                nextCursor?: string;
                hasMore: boolean;
              }) => ({
                ...page,
                expenses:
                  page.expenses?.map((e: ExpenseResponseDto) =>
                    e.id === expenseId ? toListRow(expense) : e
                  ) || [],
              })
            ),
          };
        }
      );

      // A queued expense hasn't reached the server, so no totals moved.
      if (isLocal) return;

      for (const queryKey of SPLIT_CHANGE_KEYS(expense.groupId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

// Delete expense mutation
export function useDeleteExpenseMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const ownerId = useOfflineOwnerId();
  const getExpenseByLocalId = useOfflineStore(
    state => state.getExpenseByLocalId
  );
  const removePendingExpense = useOfflineStore(
    state => state.removePendingExpense
  );

  return useMutation({
    // A queued expense is discarded locally, so it must not wait for the
    // network the way the default `online` mode would make it.
    networkMode: 'always',
    mutationFn: async ({
      expenseId,
      groupId,
    }: {
      expenseId: string;
      groupId: string;
    }) => {
      if (offlineExpenseService.isLocalId(expenseId)) {
        const queued = getExpenseByLocalId(expenseId, ownerId);
        if (!queued) return { expenseId, groupId, deletedOnServer: false };
        // Created on the server before the edit it still owes; dropping only
        // the queued row would leave the pre-edit copy in the group.
        const serverId = queued.needsServerPatch ? queued.serverId : undefined;
        if (serverId) {
          try {
            await api.expenses.delete(serverId);
          } catch (err) {
            const alreadyGone =
              (err as { status?: number } | null)?.status === 404 ||
              isGroupGoneSyncError(err);
            if (!alreadyGone) throw err;
          }
        }
        removePendingExpense(expenseId);
        return { expenseId, groupId, deletedOnServer: Boolean(serverId) };
      }
      await api.expenses.delete(expenseId);
      return { expenseId, groupId, deletedOnServer: true };
    },
    onMutate: async ({ expenseId, groupId }) => {
      // Store the individual expense data for rollback before removing
      const previousExpense = queryClient.getQueryData<ExpenseResponseDto>(
        queryKeys.expense(expenseId)
      );

      // Cancel the specific expense query to prevent refetch attempts after deletion
      await queryClient.cancelQueries({
        queryKey: queryKeys.expense(expenseId),
      });

      // Remove the expense from cache immediately to prevent any fetch attempts
      queryClient.removeQueries({ queryKey: queryKeys.expense(expenseId) });

      // Cancel all queries for this group's expenses (with or without filters)
      await queryClient.cancelQueries({
        queryKey: queryKeys.expenses(),
        predicate: query =>
          query.queryKey.includes('group') && query.queryKey.includes(groupId),
      });

      // Get all query data for this group
      const queries = queryClient.getQueriesData({
        queryKey: queryKeys.expenses(),
        predicate: query =>
          query.queryKey.includes('group') && query.queryKey.includes(groupId),
      });

      // Store previous data for rollback
      const previousData = queries.map(([key, data]) => ({ key, data }));

      // Update all matching queries to remove the deleted expense
      queries.forEach(([queryKey, _data]) => {
        queryClient.setQueryData(
          queryKey,
          (
            old:
              | {
                  pages: Array<{
                    expenses: ExpenseResponseDto[];
                    nextCursor?: string;
                    hasMore: boolean;
                  }>;
                  pageParams: unknown[];
                }
              | undefined
          ) => {
            if (!old?.pages) return old;

            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                expenses:
                  page.expenses?.filter(expense => expense.id !== expenseId) ||
                  [],
              })),
            };
          }
        );
      });

      return { previousData, previousExpense, expenseId, groupId };
    },
    onError: (err, _variables, context) => {
      // Report from the mutation so no caller can swallow it silently (the
      // group's Expenses tab did): the API's own message when it has one —
      // e.g. the example group's read-only notice — else the translated key.
      toast.error(
        handleApiError(err, i18n.t as unknown as TFunction, 'errors')
      );
      // Revert optimistic update on error
      if (context?.previousData) {
        context.previousData.forEach(({ key, data }) => {
          queryClient.setQueryData(key, data);
        });
      }

      // Restore the individual expense query if it existed
      if (context?.previousExpense && context?.expenseId) {
        queryClient.setQueryData(
          queryKeys.expense(context.expenseId),
          context.previousExpense
        );
      }
    },
    onSuccess: ({ expenseId, groupId, deletedOnServer }) => {
      if (!deletedOnServer) return;
      // Track analytics
      trackExpenseDeleted(groupId, expenseId);

      // Remove from specific expense cache
      queryClient.removeQueries({ queryKey: queryKeys.expense(expenseId) });

      // The expense was already removed optimistically from the list in onMutate
      // No need to invalidate the expense list queries — except for a queued
      // row, whose server copy sits in the list under its server id.
      if (offlineExpenseService.isLocalId(expenseId)) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.expenses(),
          predicate: query =>
            query.queryKey.includes('group') &&
            query.queryKey.includes(groupId),
        });
      }

      // Only invalidate calculation-related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(groupId),
      });
      // Cross-group totals on the home global-balances card.
      queryClient.invalidateQueries({
        queryKey: queryKeys.userNetBalance(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupWithDashboard(groupId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupReports.all(groupId),
        exact: false,
      });
    },
  });
}

// Expense comments
export function useExpenseCommentsQuery(expenseId: string, enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.expenseComments(expenseId),
    queryFn: async () => {
      const response = await api.get<CommentResponseDto[]>(
        `/expenses/${expenseId}/comments`
      );
      if ('data' in response && response.data) {
        return response.data as CommentResponseDto[];
      }
      return response as CommentResponseDto[];
    },
    enabled: enabled && !!expenseId,
  });
}

// Add comment mutation
export function useAddCommentMutation(expenseId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (text: string) => {
      const response = await api.expenses.addComment(expenseId, {
        comment: text,
      });
      return (response as unknown as { data: unknown })
        .data as CommentResponseDto;
    },
    onSuccess: () => {
      // Invalidate both the comments list query and the main expense query
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenseComments(expenseId),
      });
      // CRITICAL: Also invalidate the main expense query so comments show up immediately
      queryClient.invalidateQueries({
        queryKey: queryKeys.expense(expenseId),
      });
    },
  });
}

// Delete comment mutation
export function useDeleteCommentMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      expenseId,
    }: {
      commentId: string;
      expenseId: string;
    }) => {
      await api.expenses.deleteComment(commentId, expenseId);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the comments list query and the main expense query
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenseComments(variables.expenseId),
      });
      // Also invalidate the main expense query so changes show up immediately
      queryClient.invalidateQueries({
        queryKey: queryKeys.expense(variables.expenseId),
      });
    },
  });
}

// Expense reactions
export function useAddReactionMutation(expenseId: string) {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async (emoji: string) => {
      const response = await api.post<SuccessResponseDto<ExpenseResponseDto>>(
        `/expenses/${expenseId}/reactions`,
        {
          reaction: emoji,
        }
      );
      if ('data' in response && response.data) {
        return response.data;
      }
      return response;
    },
    // No onSuccess invalidation - optimistic updates are handled manually in components
  });
}

// Remove reaction mutation
export function useRemoveReactionMutation(expenseId: string) {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async (emoji: string) => {
      await api.expenses.removeReaction(expenseId, emoji);
    },
    // No onSuccess invalidation - optimistic updates are handled manually in components
  });
}

// Toggle reaction mutation (for activity feed)
export function useToggleReactionMutation(expenseId: string) {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async ({
      emoji,
      hasReacted,
    }: {
      emoji: string;
      hasReacted: boolean;
    }) => {
      if (hasReacted) {
        await api.expenses.removeReaction(expenseId, emoji);
      } else {
        await api.expenses.addReaction(expenseId, emoji);
      }
    },
    // No onSuccess invalidation - optimistic updates are handled manually in components
  });
}

// Toggle reaction mutation with expenseId passed at call time (for activity feed where expense varies)
export function useToggleReactionByIdMutation() {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async ({
      expenseId,
      emoji,
      hasReacted,
    }: {
      expenseId: string;
      emoji: string;
      hasReacted: boolean;
    }) => {
      if (hasReacted) {
        await api.expenses.removeReaction(expenseId, emoji);
      } else {
        await api.expenses.addReaction(expenseId, emoji);
      }
    },
    // No onSuccess invalidation - optimistic updates are handled manually in components
  });
}

// Update comment mutation
export function useUpdateCommentMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      text,
      expenseId,
    }: {
      commentId: string;
      text: string;
      expenseId: string;
    }) => {
      const response = await api.expenses.updateComment(commentId, {
        comment: text,
        expenseId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate both the comments list query and the main expense query
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenseComments(variables.expenseId),
      });
      // Also invalidate the main expense query so changes show up immediately
      queryClient.invalidateQueries({
        queryKey: queryKeys.expense(variables.expenseId),
      });
    },
  });
}

/**
 * Upload expense receipt mutation.
 * Handles dual-upload: creates 256px thumbnail + full original backup.
 * Both are required for proper display and zoom functionality.
 */
export function useUploadExpenseReceiptMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      expenseId,
      groupId,
      file,
    }: {
      expenseId: string;
      groupId: string;
      file: File;
    }) => {
      // 1. Optimize image: create 256px thumbnail + keep original
      const { optimized, original } = await optimizeImageForUpload(
        file,
        'expense'
      );

      // 2. For original backup: convert HEIC to JPEG (backend only accepts jpg/jpeg/png/gif/webp)
      let backupFile = original;
      if (isHeicFile(file)) {
        backupFile = await convertHeicToJpeg(file);
      }

      // 3. Get file extensions
      const thumbnailExt =
        optimized.name.split('.').pop()?.toLowerCase() || 'webp';
      const originalExt =
        backupFile.name.split('.').pop()?.toLowerCase() || 'jpg';

      // 4. Get presigned URLs for both versions. The API signs the size it
      // is sent, so S3 refuses a PUT of any other length. A HEIC converted to
      // JPEG can outgrow the signable size; it goes unsigned, as before sizes
      // were sent at all.
      const thumbnailRequest: GetUploadUrlDto = {
        type: 'expense',
        entityId: expenseId,
        fileExtension: thumbnailExt,
        contentLength: optimized.size,
      };
      const originalRequest: GetUploadUrlDto = {
        type: 'expense',
        entityId: expenseId,
        fileExtension: originalExt,
        variant: 'original',
        ...(backupFile.size <= SIGNED_UPLOAD_MAX_BYTES
          ? { contentLength: backupFile.size }
          : {}),
      };
      const [thumbnailResponse, originalResponse] = await Promise.all([
        api.upload.getPresignedUrl(thumbnailRequest),
        api.upload.getPresignedUrl(originalRequest),
      ]);

      const thumbnailData = (
        thumbnailResponse as unknown as { data: UploadUrlResponseDto }
      ).data;
      const originalData = (
        originalResponse as unknown as { data: UploadUrlResponseDto }
      ).data;

      // 5. Upload both versions to S3 in parallel
      const [thumbnailUpload, originalUpload] = await Promise.all([
        fetch(thumbnailData.uploadUrl, {
          method: 'PUT',
          body: optimized,
          headers: thumbnailData.headers ?? { 'Content-Type': optimized.type },
        }),
        fetch(originalData.uploadUrl, {
          method: 'PUT',
          body: backupFile,
          headers: originalData.headers ?? {
            'Content-Type': backupFile.type,
          },
        }),
      ]);

      if (!thumbnailUpload.ok || !originalUpload.ok) {
        throw new Error('Failed to upload receipt images');
      }

      // 6. Confirm upload with both URLs
      await api.upload.confirm({
        type: 'expense',
        entityId: expenseId,
        imageUrl: thumbnailData.imageUrl,
        imageUrlOriginal: originalData.imageUrl,
      });

      return { thumbnailUrl: thumbnailData.imageUrl, groupId };
    },
    onSuccess: ({ groupId }, { expenseId }) => {
      // Invalidate expense query to show updated receipt
      queryClient.invalidateQueries({
        queryKey: queryKeys.expense(expenseId),
      });
      // Also invalidate the expense list for this group
      queryClient.invalidateQueries({
        queryKey: ['splitfiesta', 'expenses', 'group', groupId],
        exact: false,
      });
    },
  });
}

/**
 * Delete expense receipt mutation.
 * Removes both thumbnail and original images from the expense.
 */
export function useDeleteExpenseReceiptMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      expenseId,
      groupId,
    }: {
      expenseId: string;
      groupId: string;
    }) => {
      // `null` is the clear signal; `undefined` would be dropped by JSON
      // and the API would see no change. The server removes the original
      // and both S3 objects with it.
      await api.expenses.update(expenseId, { receipt: null });
      return { groupId };
    },
    onSuccess: ({ groupId }, { expenseId }) => {
      // Invalidate expense query to show updated state
      queryClient.invalidateQueries({
        queryKey: queryKeys.expense(expenseId),
      });
      // Also invalidate the expense list for this group
      queryClient.invalidateQueries({
        queryKey: ['splitfiesta', 'expenses', 'group', groupId],
        exact: false,
      });
    },
  });
}
